import React from 'react';
import { colors, spacing, typography, radius, transitions } from '../../theme/tokens';

/**
 * Button — primitive con variantes y tamaños basados en tokens (ADR-018).
 *
 * @param {'primary'|'secondary'|'destructive'|'ghost'} variant
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} loading — muestra spinner y bloquea interacción
 * @param {boolean} disabled
 * @param {React.ReactNode} iconLeft
 * @param {React.ReactNode} iconRight
 * @param {boolean} iconOnly — si true, el hijo es el icono; oculta padding horizontal extra
 * @param {boolean} fullWidth — width 100%
 * @param {string} type — button|submit|reset (default button, evita submits involuntarios)
 */
const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  iconLeft,
  iconRight,
  iconOnly = false,
  fullWidth = false,
  type = 'button',
  children,
  style: styleProp,
  onClick,
  ...rest
}, ref) => {
  const isDisabled = disabled || loading;

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    border: '1px solid transparent',
    borderRadius: radius.sm,
    fontFamily: typography.fontFamily.sans,
    fontWeight: typography.fontWeight.medium,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: `background-color ${transitions.base}, border-color ${transitions.base}, color ${transitions.base}, opacity ${transitions.base}`,
    opacity: isDisabled && !loading ? 0.55 : 1,
    userSelect: 'none',
    whiteSpace: 'nowrap',
    ...(fullWidth ? { width: '100%' } : {}),
  };

  const sizes = {
    sm: {
      padding: iconOnly ? `${spacing[1]}px` : `${spacing[1]}px ${spacing[2]}px`,
      fontSize: typography.fontSize.sm,
      minHeight: 28,
    },
    md: {
      padding: iconOnly ? `${spacing[2]}px` : `${spacing[2]}px ${spacing[3]}px`,
      fontSize: typography.fontSize.base,
      minHeight: 36,
    },
    lg: {
      padding: iconOnly ? `${spacing[3]}px` : `${spacing[3]}px ${spacing[4]}px`,
      fontSize: typography.fontSize.md,
      minHeight: 44,
    },
  };

  const variants = {
    primary: {
      backgroundColor: colors.primary,
      color: colors.textInverse,
      borderColor: colors.primary,
    },
    secondary: {
      backgroundColor: colors.surface,
      color: colors.primary,
      borderColor: colors.primary,
    },
    destructive: {
      backgroundColor: colors.error,
      color: colors.textInverse,
      borderColor: colors.error,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.text,
      borderColor: 'transparent',
    },
  };

  const style = {
    ...base,
    ...sizes[size],
    ...variants[variant],
    ...styleProp,
  };

  const handleMouseEnter = (e) => {
    if (isDisabled) return;
    if (variant === 'primary') e.currentTarget.style.backgroundColor = colors.primaryDark;
    else if (variant === 'destructive') e.currentTarget.style.backgroundColor = colors.errorDark;
    else if (variant === 'secondary') e.currentTarget.style.backgroundColor = colors.primaryBg;
    else if (variant === 'ghost') e.currentTarget.style.backgroundColor = colors.gray100;
  };
  const handleMouseLeave = (e) => {
    if (isDisabled) return;
    e.currentTarget.style.backgroundColor = variants[variant].backgroundColor;
  };

  const spinner = (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'boveda-btn-spin 0.6s linear infinite',
      }}
    />
  );

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? spinner : iconLeft}
      {!iconOnly && children}
      {!loading && iconRight}
      <style>{`@keyframes boveda-btn-spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
